import { useEffect, useRef, useState } from "react";
import { ChatCircleDots, X, PaperPlaneRight, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";

export default function ChatWidget() {
  const settings = useSettings();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: `Hi! I'm the ${settings.brand_name} AI assistant. Ask me about company setup, virtual offices, tax, banking or which country might fit you best.` },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chat/message", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, something went wrong. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        data-testid="chat-toggle-btn"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 bg-gos-navy hover:bg-gos-navyHover text-white w-14 h-14 rounded-full flex items-center justify-center shadow-xl transition-transform hover:scale-105"
        aria-label="Open chat"
      >
        {open ? <X size={22} weight="bold" /> : <ChatCircleDots size={22} weight="duotone" />}
      </button>

      {open && (
        <div data-testid="chat-window" className="fixed bottom-24 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] h-[520px] bg-white border border-gos-border shadow-2xl flex flex-col rounded-sm overflow-hidden">
          <div className="bg-gos-navy text-white px-4 py-3 flex items-center gap-2">
            <Sparkle size={16} weight="duotone" color="#C8A97E" />
            <div className="leading-tight">
              <div className="font-display text-sm">AI Business Assistant</div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-gos-sand">{settings.brand_name}</div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gos-bone">
            {messages.map((m, i) => (
              <div key={`${i}-${m.role}-${(m.content || "").slice(0, 16)}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`text-sm max-w-[85%] px-3 py-2 rounded-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-gos-navy text-white"
                      : "bg-white border border-gos-border text-gos-charcoal"
                  }`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="text-xs text-gos-muted px-1">Assistant is typing…</div>
            )}
          </div>
          <div className="border-t border-gos-border p-2 flex gap-2 bg-white">
            <input
              data-testid="chat-input"
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask anything…"
              className="flex-1 px-3 py-2 text-sm border border-gos-border rounded-sm focus:outline-none focus:ring-1 focus:ring-gos-navy"
            />
            <button data-testid="chat-send-btn" onClick={send} disabled={sending} className="bg-gos-navy text-white px-3 rounded-sm hover:bg-gos-navyHover disabled:opacity-50">
              <PaperPlaneRight size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
